import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { map } from 'rxjs/operators';
import { forkJoin, Observable } from 'rxjs';
import { QuoteStatus } from '../models/quote.model';
import { Application } from '../models/application.model';

export enum PlanningType {
    heatpump = 'HEAT_PUMP_PLANNER',
    photovoltaic = 'PHOTOVOLTAIC_PLANNER',
    boiler = 'BOILER_PLANNER',
    a2a = 'A2A_PLANNER',
}

export interface PlanningQuote {
    id: string;
    quickRef: string;
    status: QuoteStatus;
    customerPurchaseNumber: string;
    externalApp: string;
    salesforceOpportunityId: string;
    submissionNumber: string;
    offerNumber: string;
    planningId: string;
    planningType: PlanningType;
    variantDescription: string;
    migrationConfigitId?: string;
}

export interface PlanningProject {
    customerPurchaseNumber: string;
    externalApp: string;
    salesforceOpportunityId: string;
    submissionNumber: string;
    quotes: PlanningQuote[];
    variants?: PlanningVariants[];
}

export interface PlanningVariants {
    variantName: string;
    quotes: PlanningQuote[];
}

@Injectable({
    providedIn: 'root',
})
export class ProjectsService {
    constructor(private http: HttpClient) {}

    private getPlanningProjects(salesforceOpportunityId: string): Observable<PlanningQuote[]> {
        return forkJoin(
            [Application.quoteAssist, Application.pvPlanner, Application.a2aPlanner].map((app) =>
                this.http.get<{ items: PlanningQuote[] }>(`${environment.http.quotation}quotations/${app}/quotes`, {
                    params: { salesforceOpportunityId },
                })
            )
        ).pipe(map(([resp1, resp2, resp3]) => [...resp1.items, ...resp2.items, ...resp3.items]));
    }

    public getPlanningProject(salesforceId: string): Observable<PlanningProject> {
        return this.getPlanningProjects(salesforceId).pipe(
            map((resp) => {
                const first = resp[0];
                const quotes = resp
                    .filter(
                        (item) =>
                            Object.values(PlanningType).includes(item.planningType) &&
                            !resp.some((i) => i.migrationConfigitId === item.id) // prefer converted quotes over legacy quotes
                    )
                    .sort((a, b) => a.planningType.localeCompare(b.planningType));
                return {
                    customerPurchaseNumber: first.customerPurchaseNumber,
                    externalApp: first.externalApp,
                    salesforceOpportunityId: first.salesforceOpportunityId,
                    submissionNumber: first.submissionNumber,
                    quotes,
                };
            })
        );
    }
}
